import layoutJson from '../../../recipes/art/Z03/layout.json' with { type: 'json' };
import sliceJson from '../../../assets/manifests/slice.json' with { type: 'json' };
import { spriteManifestFromSlice, type SliceManifest, type SpriteManifest } from '../../../packages/view-phaser/src/manifest.ts';

/** The frozen slice art, as sprite entries the Phaser adapter can preload. */
export const SLICE_SPRITE_MANIFEST: SpriteManifest = spriteManifestFromSlice(sliceJson as unknown as SliceManifest);

/**
 * A ground material laid over a block of tiles; first match wins.
 *
 * Rounds 3 to 6 claimed a zone as a centre and a radius with two octaves of
 * jitter on it, and let a zone drop a fraction of its own tiles back to the base
 * material. That prints a material as a soft-edged blotch fading into its
 * neighbour, which is why the brick read as a stain airbrushed over the
 * flagstone rather than as a floor somebody laid. A paved floor has edges where
 * the paving stops, and those edges land on the grid the paving is set out on.
 * So a zone is now a block of tiles with a hard boundary, and an irregular
 * region is authored as several blocks sharing one assetId.
 */
interface GroundZone {
  assetId: string;
  /** Inclusive tile bounds, [x0, y0, x1, y1]. Boundaries fall on the tile grid. */
  rect: readonly number[];
  /** Per-zone multiply on top of the key light, to pull one material's hue back. */
  tint?: readonly number[];
}

interface Beat {
  beatId: string;
  footprintTiles: { width: number; height: number };
  surfaces: readonly string[];
  groundZones?: readonly GroundZone[];
  /**
   * Dressing props. `flipX` and `scale` are the per-instance variation: the kit
   * has one sheet per object, so three crate stacks were three identical
   * sprites, and the eye reads identical sprites as a tiling artefact rather
   * than as three crates. A mirrored or slightly resized copy is a free variant
   * of art already delivered.
   */
  props: readonly { assetId: string; tile: readonly number[]; flipX?: boolean; scale?: number }[];
}

const BEATS = (layoutJson as { beats: readonly Beat[] }).beats;

export function z03Beat(beatId: string): Beat {
  const beat = BEATS.find(candidate => candidate.beatId === beatId);
  if (!beat) throw new Error(`Unknown Z03 beat: ${beatId}`);
  return beat;
}
